import React, { useContext, useEffect, useState } from 'react'
import { appContext } from '../contexts/appContext'
import { AppContext, IFormData } from '../types/app-context'
import { PaymentMethod } from '../types/payment'
import { formatCurrency } from '../utils/formatting'
import { getFormDataFromUrl } from '../utils/urlParams'
import AddressForm from './AddressForm'
import CustomerDataForm from './CustomerDataForm'
import FormField from './FormField'
import BoletoPayment from './PaymentDetails/BoletoPayment'
import CreditCardPayment from './PaymentDetails/CreditCardPayment'
import PixPayment from './PaymentDetails/PixPayment'
import PaymentMethods from './PaymentMethods'

const PaymentForm: React.FC = () => {
  const [showNewPaymentOption, setShowNewPaymentOption] = useState(false)
  const {
    setFormData,
    errors,
    setErrors,
    setPaymentResponse,
    setIsLoading,
    selectedMethod,
    setSelectedMethod,
    handleSubmit,
    formData,
    paymentResponse,
    isLoading,
  } = useContext(appContext) as AppContext

  useEffect(() => {
    // Load initial data from URL parameters
    const urlData = getFormDataFromUrl()
    const initialData: IFormData = {}

    if (urlData.valor) {
      const numValue = parseFloat(urlData.valor.replace(',', '.'))
      if (!isNaN(numValue)) {
        initialData['payment-value'] = numValue.toFixed(2).replace('.', ',')
      }
    }

    if (urlData.nome) initialData['customer-name'] = urlData.nome
    if (urlData.email) initialData['customer-email'] = urlData.email
    if (urlData.doc) initialData['customer-document'] = urlData.doc
    if (urlData.tel) initialData['customer-phone'] = urlData.tel
    if (urlData.cep) initialData['customer-zipcode'] = urlData.cep
    if (urlData.estado) initialData['customer-state'] = urlData.estado
    if (urlData.cidade) initialData['customer-city'] = urlData.cidade
    if (urlData.bairro) initialData['customer-neighborhood'] = urlData.bairro
    if (urlData.endereco) initialData['customer-address'] = urlData.endereco
    if (urlData.numero) initialData['customer-number'] = urlData.numero
    if (urlData.complemento)
      initialData['customer-complement'] = urlData.complemento
    if (urlData.parcelas) initialData['card-installments'] = urlData.parcelas
    if (urlData.IdPagamento) initialData['IdPagamento'] = urlData.IdPagamento
    if (urlData.IdReservaCurto)
      initialData['IdReservaCurto'] = urlData.IdReservaCurto

    if (
      urlData.metodo &&
      ['pix', 'boleto', 'credit-card'].includes(urlData.metodo)
    ) {
      setSelectedMethod(urlData.metodo as PaymentMethod)
    }

    setFormData(initialData)
  }, [])

  const handleInputChange = (field: string, value: string) => {
    if (field === 'payment-value') {
      value = formatCurrency(value)
    }

    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const handleNewPayment = () => {
    setPaymentResponse(null)
    setShowNewPaymentOption(false)
    setIsLoading(false)
    // Manter o valor do pagamento quando gerar novo PIX
    // O valor já está no formData['payment-value'], então não precisa fazer nada
  }

  const showAddressSection =
    selectedMethod === 'boleto' || selectedMethod === 'credit-card'
  const showEmailField =
    selectedMethod === 'credit-card' ||
    selectedMethod === 'boleto' ||
    selectedMethod === 'pix'
  const showPhoneField =
    selectedMethod === 'credit-card' ||
    selectedMethod === 'boleto' ||
    selectedMethod === 'pix'
  const showComplementField =
    selectedMethod === 'boleto' || selectedMethod === 'credit-card'

  if (isLoading) {
    return (
      <div className="loading">
        <i className="fas fa-spinner"></i>
        <p>Processando pagamento...</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <FormField
        label="Valor do Pagamento (R$)"
        required
        error={errors['payment-value']}
      >
        <input
          type="text"
          id="payment-value"
          value={formData['payment-value'] || ''}
          onChange={(e) => handleInputChange('payment-value', e.target.value)}
          placeholder="0,00"
          className={errors['payment-value'] ? 'invalid' : ''}
        />
      </FormField>

      <h3 className="section-title">Método de Pagamento</h3>
      <PaymentMethods
        selectedMethod={selectedMethod}
        onMethodChange={setSelectedMethod}
      />

      <div className="section-customer visible">
        <h3 className="section-title">Dados do Cliente</h3>
        <CustomerDataForm
          formData={formData}
          errors={errors}
          onInputChange={handleInputChange}
          showEmailField={showEmailField}
          showPhoneField={showPhoneField}
        />
      </div>

      {showAddressSection && (
        <div className="section-address visible">
          <h3 className="section-title">Endereço</h3>
          <AddressForm
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            showComplementField={showComplementField}
          />
        </div>
      )}

      {selectedMethod === 'pix' && (
        <div
          className="payment-details active mb-4"
          style={{ display: 'block' }}
        >
          <PixPayment />
        </div>
      )}

      {selectedMethod === 'boleto' && (
        <div className="payment-details active" style={{ display: 'block' }}>
          <BoletoPayment paymentResponse={paymentResponse} />
        </div>
      )}

      {selectedMethod === 'credit-card' && (
        <div className="payment-details active" style={{ display: 'block' }}>
          <CreditCardPayment
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            paymentValue={parseFloat(
              formData['payment-value']?.replace(',', '.') || '0'
            )}
          />
        </div>
      )}

      {showNewPaymentOption && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p
            style={{
              color: '#856404',
              backgroundColor: '#fff3cd',
              padding: '10px',
              borderRadius: '5px',
            }}
          >
            O tempo para pagamento expirou. Deseja gerar um novo PIX?
          </p>
          <button
            type="button"
            className="btn"
            onClick={handleNewPayment}
            style={{ marginTop: '10px' }}
          >
            Gerar Novo PIX
          </button>
        </div>
      )}

      {!paymentResponse && !showNewPaymentOption && (
        <button type="submit" className="btn" disabled={isLoading}>
          Finalizar Pagamento
        </button>
      )}
    </form>
  )
}

export default PaymentForm
